import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import TableSkeletonComponent from "@/app/components/ui/LoaderComponent";
import ModalPrompt from "@/app/components/ui/Modals/Prompt";
import NoItemFoundComponent from "@/app/components/ui/NoItemFoundComponent";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { ISelectMenuItemData } from "@/app/components/ui/interface";
import { useVaccinationHistoryListAPI } from "@/app/hooks/api_hooks/Vaccination/useVaccinationHistoryListAPI";
import { useVaccinationInputListAPI } from "@/app/hooks/api_hooks/Vaccination/useVaccinationInputListAPI";
import { useVaccinationSummaryListAPI } from "@/app/hooks/api_hooks/Vaccination/useVaccinationSummaryListAPI";
import {
  IVaccinationDobSpeciesBreedPutBody,
  useVaccinationDobUpdateAPI,
} from "@/app/hooks/api_hooks/customer_profile/useVaccinationDobUpdateAPI";
import {
  IVaccinationSpeciesBreedPutBody,
  useVaccinationSpeciesBreedUpdateAPI,
} from "@/app/hooks/api_hooks/customer_profile/useVaccinationSpeciesBreedAPI";
import {
  IVaccinationPutBody,
  useVaccinationUpdateAPI,
} from "@/app/hooks/api_hooks/customer_profile/useVaccinationUpdateAPI";
import { useUpdateTempEMRAPI } from "@/app/hooks/api_hooks/emr/useUpdateTempEMRAPI";
import { useModal } from "@/app/hooks/ui_hooks/useModal";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useModalState } from "@/app/hooks/ui_hooks/useModalState";
import { useArrayConstants } from "@/app/hooks/useArrayConstants";
import { useBreedConstants } from "@/app/hooks/useBreedConstants";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useStringConstants } from "@/app/hooks/useStringConstants";
import { ROUTES } from "@/app/routes";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";

export interface IVaccinationProps {
  date: string;
  [key: `date-${number}`]: string;
  dob: string;
  species: string;
  breed: string;
}

const VaccinationEnterDataModalContent = ({ formHook, petDetails, petId, customerId, vaccinationInputData, isEMR }) => {
  const navigate = useNavigate();
  const { NO_CART } = useImageConstants();
  const isVaccinationLoader = useLoader("isvaccinationTableLoading");
  const stringConstants = useStringConstants();
  const { pet_species } = useArrayConstants();
  const { catBreeds, dogBreeds, giantBreeds } = useBreedConstants();
  const catBreedItems: ISelectMenuItemData[] = useSelectMenuReducer(catBreeds, "pet_breed", "id");
  const dogBreedItems: ISelectMenuItemData[] = useSelectMenuReducer(dogBreeds, "pet_breed", "id");
  const giantBreedItems: ISelectMenuItemData[] = useSelectMenuReducer(giantBreeds, "pet_breed", "id");
  const { execute: updatePetDob } = useVaccinationDobUpdateAPI();
  const { execute: updatePetSpeciesBreed } = useVaccinationSpeciesBreedUpdateAPI();
  const { execute: fetchVaccinationInputList } = useVaccinationInputListAPI();
  const [showDOBModal, setShowDOBModal] = useState(false);
  const [showSpeciesBreedModal, setShowSpeciesBreedModal] = useState(false);
  const formValues = formHook.watch();

  useEffect(() => {
    if (petId) {
      fetchVaccinationInputList(petId, customerId).then(({ status, message }) => {
        if (!status && message === "Date of Birth is missing") {
          setShowDOBModal(true);
        } else if (!status && message === "Species & Breed is missing") {
          setShowSpeciesBreedModal(true);
        }
      });
    }
  }, [petId]);

  const getPetBreedItems = (speciesId: string) => {
    switch (speciesId) {
      case "1":
        return dogBreedItems;
      case "2":
        return catBreedItems;
      case "15":
        return giantBreedItems;
      default:
        return dogBreedItems;
    }
  };

  const onDOBConfirm = (data: IVaccinationProps) => {
    const constructedBody: IVaccinationDobSpeciesBreedPutBody = {
      dob: data?.dob,
    };
    updatePetDob(constructedBody, petDetails?.id, customerId).then(({ status }) => {
      if (status) {
        setShowDOBModal(false);
        fetchVaccinationInputList(petDetails?.id, customerId);
      }
    });
  };

  const onSpeciesBreedConfirm = (data: IVaccinationProps) => {
    const constructedBody: IVaccinationSpeciesBreedPutBody = {
      pet_breed: data?.breed,
      species_name: data?.species,
    };
    updatePetSpeciesBreed(constructedBody, petDetails?.id, customerId).then(({ status }) => {
      if (status) {
        setShowSpeciesBreedModal(false);
        fetchVaccinationInputList(petDetails?.id, customerId);
      }
    });
  };

  const handleAddPetClick = () => {
    navigate(`/app/customer/profile/customer-details/add-pet/${customerId}`);
  };

  return (
    <div className="bg-white">
      {isVaccinationLoader ? (
        <TableSkeletonComponent columnLength={2} bodyLength={6} />
      ) : (
        <div className="flex flex-col gap-3 mt-3">
          {showDOBModal && (
            <div className="bg-[#F9FAFB] rounded-xl p-3">
              <p className="text-[#656565] text-sm font-medium tracking-tight42">
                Please enter the pet's DOB to view their vaccination schedule
              </p>
              <div className="bg-white rounded-xl border border-[#E2E2E2] p-3 mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
                <p className="text-[#000000] text-sm font-medium tracking-tight42 sm:w-28 shrink-0">
                  DOB <span className="text-[#ED0101]">*</span>
                </p>
                <div className="w-full sm:flex-1">
                  <PWLCalendarComponent
                    showFutureYears={false}
                    allowPastDaySelection={true}
                    errorMessages={[{ message: "Date is required", type: "required" }]}
                    register={formHook.register("dob", {
                      required: true,
                    })}
                    disableDatesAfterDate={format(new Date(Date.now()), stringConstants?.fieldDOBFormat)}
                    fieldError={formHook.formState.errors.dob}
                    onSelectDate={(dates) => {
                      formHook.setValue("dob", format(dates, stringConstants?.fieldDOBFormat));
                    }}
                    defaultSelectedDate={formHook.getValues("dob")}
                    className="w-full"
                  />
                </div>
                <button onClick={formHook.handleSubmit(onDOBConfirm)} className={`${PRIMARY_BUTTON} w-full sm:w-auto`}>
                  Confirm
                </button>
              </div>
            </div>
          )}
          {showSpeciesBreedModal && (
            <div className="bg-[#F9FAFB] rounded-xl p-3">
              <p className="text-[#656565] text-sm font-medium tracking-tight42">
                Please enter the pet's Species to view their vaccination schedule
              </p>
              <div className="bg-white rounded-xl border border-[#E2E2E2] p-3 mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
                <p className="text-[#000000] text-sm font-medium tracking-tight42 sm:w-28 shrink-0">
                  Species & Breed <span className="text-[#ED0101]">*</span>
                </p>
                <div className="flex flex-col gap-3 w-full sm:flex-row sm:flex-1">
                  <NonSearchableSelectMenu
                    listBoxClassName="w-full sm:w-44"
                    showDropdownIcon={true}
                    selectItems={pet_species}
                    placeholder="Species"
                    className="text-xs"
                    onSelectItem={(item) => {
                      if (item) {
                        formHook.setValue("species", item.title);
                      }
                    }}
                    errorMessages={[{ type: "required", message: "Species required." }]}
                    fieldError={formHook.formState.errors.species}
                    register={formHook.register("species", {
                      required: true,
                    })}
                  />
                  <SearchableSelectMenu
                    placeholder="Breed"
                    showTooltips={true}
                    showDropdownIcon={true}
                    onSelectItem={(item) => {
                      if (item) {
                        formHook.setValue("breed", item.title);
                      }
                    }}
                    selectItems={getPetBreedItems(formHook.watch("species"))}
                    errorMessages={[{ type: "required", message: "Breed required." }]}
                    fieldError={formHook.formState.errors.breed}
                    register={formHook.register("breed", { required: true })}
                    listBoxClassName="w-full"
                    containerClassName="w-full sm:w-52"
                  />
                </div>
                <button
                  onClick={formHook.handleSubmit(onSpeciesBreedConfirm)}
                  className={`${PRIMARY_BUTTON} w-full sm:w-auto`}
                >
                  Confirm
                </button>
              </div>
            </div>
          )}
          {Array.isArray(vaccinationInputData) && vaccinationInputData.length > 0 ? (
            <div className="bg-[#F9FAFB] rounded-xl p-2 sm:p-3">
              <div className="hidden sm:flex items-center px-3 pb-2">
                <p className="flex-1 text-[#656565] text-sm font-medium tracking-tight42">Vaccine Name</p>
                <p className="w-[200px] shrink-0 text-[#656565] text-sm font-medium tracking-tight42">Given Date</p>
              </div>
              <div className="flex flex-col gap-2">
                {vaccinationInputData?.map((vac, index) => {
                  return (
                    <div
                      key={index}
                      className="bg-white rounded-xl border border-[#E2E2E2] p-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4"
                    >
                      <div className="min-w-0 sm:flex-1">
                        <p className="text-[#000000] text-sm font-medium tracking-tight42 break-words">
                          {vac?.vaccine_name}
                        </p>
                        {vac?.due_date && <p className="text-xs text-[#6C727F] mt-0.5">Due: {vac?.due_date}</p>}
                      </div>
                      <div className="w-full sm:w-[200px] sm:shrink-0">
                        <PWLCalendarComponent
                          allowPastDaySelection={true}
                          errorMessages={[{ message: "", type: "required" }]}
                          register={formHook.register(`date-${index}` as const, {
                            required: false,
                          })}
                          disableDatesAfterDate={format(new Date(Date.now()), stringConstants?.fieldDOBFormat)}
                          disabledFutureDateYearMonthSelection
                          fieldError={formHook.formState.errors[`date-${index}` as const]}
                          onSelectDate={(date) => {
                            formHook.setValue(`date-${index}`, date ? format(date, "yyyy-MM-dd") : "");
                          }}
                          withClear={isEMR && !!formValues?.[`date-${index}`]}
                          containerClassName={isEMR ? "flex items-center gap-1 [&>button]:shrink-0 [&>button]:pr-0" : ""}
                          defaultSelectedDate={vac?.given_date}
                          className="w-full !pr-9"
                          showLeftNav={false}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : !showDOBModal && !showSpeciesBreedModal ? (
            <div className="bg-[#F9FAFB] rounded-xl py-8 px-4 flex flex-col items-center text-center">
              <NoItemFoundComponent message="" description="" imageURL={NO_CART} InputClassName="my-0 h-full" />
              <p className="text-[#656565] text-sm font-medium mt-4">
                {petDetails ? "No vaccination records found." : "No pets found to add vaccination records."}
              </p>
              {!petDetails && (
                <button type="button" onClick={handleAddPetClick} className={`${PRIMARY_BUTTON} mt-5`}>
                  + Add Pet
                </button>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

const VaccinationEnterDataModal = () => {
  const { execute: updateVaccinationDetail } = useVaccinationUpdateAPI();
  const { routeState } = useRouteInfo(ROUTES.ROOT.children.CUSTOMER_PROFILE.id);
  const { petDetails, customerId, petId, vaccinationInputData } = routeState.state as ICustomerProfileState;
  const { setInvisible, setModalData } = useModalActions("VACCINATION_ENTER_DATA_MODAL");
  const isModalOpen = useModal("VACCINATION_ENTER_DATA_MODAL");
  const { isEMR, basicCheckup } = useModalState("VACCINATION_ENTER_DATA_MODAL");
  const { execute: updateEmrAPI } = useUpdateTempEMRAPI();
  const { execute: fetchVaccinationHistoryList } = useVaccinationHistoryListAPI();
  const { execute: fetchVaccinationSummaryList } = useVaccinationSummaryListAPI();

  const formHook = useForm<IVaccinationProps>({
    mode: "onChange",
    defaultValues: {},
  });

  useEffect(() => {
    if (!isModalOpen && isEMR) {
      setModalData({});
      formHook.reset(
        Object.fromEntries((vaccinationInputData ?? []).map((_, index) => [`date-${index}`, ""])),
      );
    }
  }, [isModalOpen]);

  const onEMRSubmit = (data: IVaccinationProps) => {
    const vaccinationsDetails = (vaccinationInputData ?? [])
      .map((item, i) => ({ item, givenDate: data[`date-${i}` as const] }))
      .filter(({ givenDate }) => givenDate)
      .map(({ item, givenDate }) => ({
        vaccine_id: item?.vaccine_id,
        dose_id: item?.dose_id,
        new_date: givenDate,
        vaccination_name: item?.vaccine_name,
      }));
    if (!vaccinationsDetails.length) {
      setInvisible();
      return;
    }
    const params = new URLSearchParams(location.search);
    const constructedBody = {
      basic_checkup: {
        ectoparasiticide_cover: {
          status: basicCheckup?.ectoparasiticide_cover?.status,
          remarks: basicCheckup?.ectoparasiticide_cover?.remarks,
          new_date: basicCheckup?.ectoparasiticide_cover?.new_date,
        },
        deworming: {
          status: basicCheckup?.deworming?.status,
          remarks: basicCheckup?.deworming?.remarks,
          new_date: basicCheckup?.deworming?.new_date,
        },
        vaccination: {
          status: basicCheckup?.vaccination?.status,
          remarks: basicCheckup?.vaccination?.remarks,
          vaccination_details: vaccinationsDetails,
        },
      },
      emr_status: params.get("emr_status"),
    };
    updateEmrAPI(params.get("emr_id"), constructedBody).then(({ status, message }) => {
      if (status) {
        for (let i = 0; i < vaccinationInputData?.length; i++) {
          formHook.setValue(`date-${i}`, "");
        }
        setModalData({ savedEmr: message });
        setInvisible();
      }
    });
  };

  const onSubmit = (data: IVaccinationProps) => {
    const vaccinationDetails = vaccinationInputData
      ?.map((item, index) => {
        const givenDate = data[`date-${index}` as const];
        if (givenDate) {
          return {
            vaccination_id: item?.vaccination_id,
            vaccine_id: item?.vaccine_id,
            dose_id: item?.dose_id,
            due_date: item?.due_date,
            given_date: givenDate,
          };
        }
        return null;
      })
      .filter(Boolean);
    const constructedBody: IVaccinationPutBody = {
      user_id: customerId,
      pet_id: petId,
      vaccination_details: vaccinationDetails,
    };
    updateVaccinationDetail(constructedBody).then(({ status }) => {
      if (status) {
        for (let i = 0; i < vaccinationInputData?.length; i++) {
          formHook.setValue(`date-${i}`, "");
        }
        fetchVaccinationHistoryList(petId, customerId);
        fetchVaccinationSummaryList(petId, customerId);
        fetchVaccinationHistoryList(petId, customerId, "deworming");
        fetchVaccinationSummaryList(petId, customerId, "deworming");
        setInvisible();
      }
    });
  };

  return (
    <ModalPrompt
      modalId="VACCINATION_ENTER_DATA_MODAL"
      showIcon={false}
      contentOverflow={true}
      titleIcon=""
      dialogClassName="w-full sm:my-8 sm:max-w-2xl sm:p-6 !rounded-[20px] max-h-[85vh] !overflow-y-auto"
      button={{
        primaryButtonText: "Save",
        secondaryButtonText: "Cancel",
        primaryButtonClassname: `${PRIMARY_BUTTON} !font-medium`,
        secondaryButtonClassname: `${SECONDARY_BUTTON} !bg-[#F1F1F1] hover:!bg-gray-200 !ring-0 !shadow-none !font-medium`,
        primaryCallback: () => {
          (isEMR ? onEMRSubmit : onSubmit)(formHook.getValues());
        },
        secondaryCallback: () => {
          setInvisible();
        },
      }}
      title="Vaccination Enter Data"
      content={
        <VaccinationEnterDataModalContent
          formHook={formHook}
          petDetails={petDetails}
          petId={petId}
          customerId={customerId}
          vaccinationInputData={vaccinationInputData}
          isEMR={isEMR}
        />
      }
    />
  );
};

export default VaccinationEnterDataModal;
